import type { Decision } from "@/lib/api";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { StatusChip } from "@/components/StatusChip";
import { formatDateTime } from "@/lib/format";

const KIND_LABEL: Record<Decision["kind"], string> = {
  parse: "Parser",
  decide: "Decider",
  resolve: "Follow-up resolver",
  rule_eval: "Rule evaluation",
};

/**
 * One row of the audit trail, in full — the reason `decisions.input`/`output` are never
 * truncated server-side. Collapsed behind <details> so a screen full of these stays
 * scannable on a phone; nothing here is summarized away.
 */
export function DecisionBlock({ decision }: { decision: Decision }) {
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
        <div>
          <div className="text-sm font-semibold">{KIND_LABEL[decision.kind]}</div>
          <div className="text-xs text-muted-foreground">
            {formatDateTime(decision.createdAt)}
            {decision.model && ` · ${decision.model}`}
            {decision.confidence !== null && ` · confidence ${(decision.confidence * 100).toFixed(0)}%`}
          </div>
        </div>
        <StatusChip status={decision.outcome} />
      </CardHeader>
      <CardContent className="space-y-2 pt-0">
        {decision.error && <p className="text-sm text-destructive">{decision.error}</p>}
        {decision.guardFailures && decision.guardFailures.length > 0 && (
          <ul className="list-inside list-disc text-sm text-destructive">
            {decision.guardFailures.map((reason, i) => (
              <li key={i}>{reason}</li>
            ))}
          </ul>
        )}
        <details className="text-xs">
          <summary className="cursor-pointer select-none text-muted-foreground">Full input</summary>
          <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-2">{JSON.stringify(decision.input, null, 2)}</pre>
        </details>
        {decision.output && (
          <details className="text-xs">
            <summary className="cursor-pointer select-none text-muted-foreground">Full output</summary>
            <pre className="mt-2 overflow-x-auto rounded-md bg-muted p-2">{JSON.stringify(decision.output, null, 2)}</pre>
          </details>
        )}
      </CardContent>
    </Card>
  );
}
